import { IFilTerValue } from "@shared/lib/types/globalDiscovery";
import { SORT_OPTIONS } from "../constants";

/**
 * Serializes filters to URL search params
 */
const defaultSort = SORT_OPTIONS[0].value; // Default to "Followers" sort

export const serializeFiltersToUrl = (
  filters: IFilTerValue,
  platformId: string,
  currentPage: number,
  pageSize: number
): URLSearchParams => {
  const params = new URLSearchParams();

  // Add platform
  if (platformId) {
    params.set("platform", platformId);
  }

  // Add pagination
  if (currentPage > 1) {
    params.set("page", currentPage.toString());
  }
  if (pageSize !== 20) {
    // 20 is default, only add if different
    params.set("pageSize", pageSize.toString());
  }

  // Ensure default sort is present
  const filtersToSerialize = {
    sort_by: filters.sort_by || defaultSort,
    ...filters,
  };

  // Serialize filters
  const entries = Object.entries(filtersToSerialize) as [string, any][];

  entries.forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    // Handle filterTags separately as nested object
    if (key === "filterTags") {
      if (typeof value === "object" && Object.keys(value).length > 0) {
        params.set("filterTags", JSON.stringify(value));
      }
      return;
    }

    // Handle different value types
    if (typeof value === "string") {
      params.set(key, value);
    } else if (typeof value === "number") {
      params.set(key, value.toString());
    } else if (Array.isArray(value)) {
      params.set(key, JSON.stringify(value));
    } else if (typeof value === "object") {
      // Handle objects like { min, max } or { field, order }
      params.set(key, JSON.stringify(value));
    }
  });

  return params;
};

/**
 * Deserializes URL search params to filters
 */
export const deserializeFiltersFromUrl = (
  searchParams: URLSearchParams
): {
  filters: IFilTerValue;
  platformId: string;
  currentPage: number;
  pageSize: number;
} => {
  const filters: IFilTerValue = {};
  let platformId = "";
  let currentPage = 1;
  let pageSize = 20;

  // Extract platform
  const platform = searchParams.get("platform");
  if (platform) {
    platformId = platform;
  }

  // Extract pagination
  const page = searchParams.get("page");
  if (page) {
    const parsedPage = Number.parseInt(page, 10);
    if (!Number.isNaN(parsedPage) && parsedPage > 0) {
      currentPage = parsedPage;
    }
  }

  const pageSizeParam = searchParams.get("pageSize");
  if (pageSizeParam) {
    const parsedPageSize = Number.parseInt(pageSizeParam, 10);
    if (!Number.isNaN(parsedPageSize) && parsedPageSize > 0) {
      pageSize = parsedPageSize;
    }
  }

  // Extract filterTags first (if present)
  const filterTagsParam = searchParams.get("filterTags");
  if (filterTagsParam) {
    try {
      const parsedFilterTags = JSON.parse(filterTagsParam);
      if (typeof parsedFilterTags === "object") {
        filters.filterTags = parsedFilterTags;
      }
    } catch {
      // Invalid JSON, skip
    }
  }

  // Extract filters
  searchParams.forEach((value, key) => {
    // Skip platform, pagination, and filterTags params (already handled)
    if (
      key === "platform" ||
      key === "page" ||
      key === "pageSize" ||
      key === "filterTags"
    ) {
      return;
    }

    // Try to parse as JSON first (for arrays/objects)
    try {
      const parsed = JSON.parse(value);
      filters[key] = parsed;
    } catch {
      // If not JSON, treat as string
      filters[key] = value;
    }
  });

  // Ensure default sort_by is set if missing
  if (!filters.sort_by) {
    filters.sort_by = defaultSort;
  }

  if (!filters.filterTags) {
    filters.filterTags = {};
  }



  return { filters, platformId, currentPage, pageSize };
}

